import { Textarea } from '@mantine/core';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Endpoints } from '@xver/apis';
import type { ProjectDetailModel, UpdateProjectRequest } from '@xver/apis/v1/projects/model';
import { Button, InputField } from '@xver/components/Atoms';
import DialogBox from '@xver/components/Atoms/DialogBox/DialogBox';
import { invalidateProjectQueries, useProjectFeedback } from '@xver/hooks/useProjectQueries';
import { getErrorCode } from '@xver/utils/error';
import {
    PROJECT_DESCRIPTION_MAX,
    PROJECT_NAME_MAX,
    validateProjectFields,
} from '@xver/utils/projects';
import { FormEvent, ReactNode, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AccessSummary, DateTime } from '../../ProjectsPage/components/ProjectBits';

function Detail({ term, children }: { term: string; children: ReactNode }) {
    return (
        <div className="flex flex-col sm:flex-row gap-1 sm:gap-4 py-2 border-b-[0.5px] border-line last:border-b-0">
            <dt className="sm:w-48 shrink-0 text-sm text-fg/68">{term}</dt>
            <dd className="text-sm text-fg break-words min-w-0">{children}</dd>
        </div>
    );
}

export default function GeneralTabPanel({ project }: { project: ProjectDetailModel }) {
    const { t } = useTranslation('projects');
    const queryClient = useQueryClient();
    const { errorMessage, notifySuccess } = useProjectFeedback();
    const canEdit = project.role === 'owner' || project.role === 'admin';
    const [name, setName] = useState(project.name);
    const [description, setDescription] = useState(project.description);
    const [submitted, setSubmitted] = useState(false);
    const [status, setStatus] = useState('');
    const nameRef = useRef<HTMLInputElement>(null);
    const descriptionRef = useRef<HTMLTextAreaElement>(null);

    useEffect(() => {
        setName(project.name);
        setDescription(project.description);
    }, [project.name, project.description]);

    const { mutate, reset, isPending, error } = useMutation({
        mutationFn: (payload: UpdateProjectRequest) =>
            Endpoints.v1.Projects.updateProject(project.id, payload),
        onSuccess: async () => {
            setSubmitted(false);
            notifySuccess(t('general.savedTitle'), t('general.savedMessage'));
            await invalidateProjectQueries(queryClient);
        },
        onError: (mutationError) => {
            if (getErrorCode(mutationError) === 'project_name_taken') nameRef.current?.focus();
        },
    });

    const errors = validateProjectFields({ name, description });
    const nameTaken = getErrorCode(error) === 'project_name_taken';
    const nameError =
        submitted && errors.name
            ? t(`form.errors.name.${errors.name}`, { max: PROJECT_NAME_MAX })
            : nameTaken
              ? errorMessage(error)
              : undefined;
    const descriptionError =
        submitted && errors.description
            ? t('form.errors.descriptionTooLong', { max: PROJECT_DESCRIPTION_MAX })
            : undefined;
    const formError = error && !nameTaken ? errorMessage(error) : undefined;

    const handleSubmit = (event: FormEvent) => {
        event.preventDefault();
        if (isPending) return;
        setSubmitted(true);
        setStatus('');
        if (errors.name) return nameRef.current?.focus();
        if (errors.description) return descriptionRef.current?.focus();

        const payload: UpdateProjectRequest = {};
        if (!project.isDefault && name.trim() !== project.name) payload.name = name.trim();
        if (description.trim() !== project.description) payload.description = description.trim();
        if (Object.keys(payload).length === 0) {
            setStatus(t('general.noChanges'));
            return;
        }
        mutate(payload);
    };

    const containers = project.resources.containers;

    return (
        <div className="w-full flex flex-col gap-6">
            <DialogBox label={t('general.title')} description={t('general.description')}>
                {canEdit ? (
                    <form
                        onSubmit={handleSubmit}
                        noValidate
                        className="w-full max-w-2xl p-4 flex flex-col gap-4"
                    >
                        <InputField
                            ref={nameRef}
                            size="md"
                            label={t('form.nameLabel')}
                            description={
                                project.isDefault
                                    ? t('general.defaultNameLocked')
                                    : t('form.nameDescription', { max: PROJECT_NAME_MAX })
                            }
                            value={name}
                            onChange={(event) => {
                                setName(event.target.value);
                                if (nameTaken) reset();
                            }}
                            readOnly={project.isDefault || isPending}
                            required={!project.isDefault}
                            autoComplete="off"
                            error={nameError}
                        />
                        <Textarea
                            ref={descriptionRef}
                            label={t('form.descriptionLabel')}
                            description={t('form.descriptionDescription', {
                                max: PROJECT_DESCRIPTION_MAX,
                            })}
                            value={description}
                            onChange={(event) => setDescription(event.currentTarget.value)}
                            autosize
                            minRows={3}
                            readOnly={isPending}
                            error={descriptionError}
                        />
                        {formError && (
                            <p role="alert" className="text-sm text-fg">
                                {formError}
                            </p>
                        )}
                        <div className="flex flex-wrap items-center gap-3">
                            <Button type="submit" isLoading={isPending}>
                                {t('general.save')}
                            </Button>
                            <p role="status" className="text-sm text-fg/78">
                                {status}
                            </p>
                        </div>
                    </form>
                ) : (
                    <dl className="w-full px-4 py-2">
                        <Detail term={t('form.nameLabel')}>{project.name}</Detail>
                        <Detail term={t('form.descriptionLabel')}>
                            {project.description || t('general.noDescription')}
                        </Detail>
                    </dl>
                )}
            </DialogBox>

            <DialogBox label={t('general.aboutTitle')}>
                <dl className="w-full px-4 py-2">
                    <Detail term={t('general.owner')}>{project.owner.name}</Detail>
                    <Detail term={t('general.created')}>
                        <DateTime iso={project.createdAt} />
                    </Detail>
                    <Detail term={t('general.yourRole')}>{t(`roles.${project.role}`)}</Detail>
                    <Detail term={t('general.yourAccess')}>
                        <AccessSummary role={project.role} access={project.access} />
                    </Detail>
                    <Detail term={t('general.members')}>{project.memberCount}</Detail>
                    <Detail term={t('general.resources')}>
                        {t('general.resourceCounts', {
                            instances: t('counts.instances', {
                                count: project.resources.instances,
                            }),
                            baremetals: t('counts.baremetals', {
                                count: project.resources.baremetals,
                            }),
                            containers:
                                typeof containers === 'number'
                                    ? t('counts.containers', { count: containers })
                                    : t('counts.containersUnknown'),
                        })}
                    </Detail>
                </dl>
            </DialogBox>
        </div>
    );
}
